import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, EmptyState, Skeleton } from '../ui';
import { useLearning } from '../../context/LearningContext';

/**
 * `/missions/review/:deckId` — the spaced-repetition entry point.
 *
 * `ReviewSession` is driven by a *review session* id, not a deck id (it reads
 * `GET /api/learning/sessions/:id`), so this route opens the session first and
 * then hands over. Keeping the deck-addressed URL matters because that is what
 * the Mission Center, the deck list and any bookmark can actually name.
 */
export function DeckReviewLauncher() {
  const { t } = useTranslation();
  const { deckId } = useParams();
  const navigate = useNavigate();
  const { startSession } = useLearning();

  const [state, setState] = useState('loading'); // loading | empty | error
  const [error, setError] = useState(null);
  const startedRef = useRef(null);

  const open = useCallback(async () => {
    setState('loading');
    setError(null);
    try {
      const session = await startSession(deckId);
      if (!session?.sessionId || (Array.isArray(session.cards) && session.cards.length === 0)) {
        setState('empty');
        return;
      }
      navigate(`/missions/session/${session.sessionId}`, { replace: true });
    } catch (err) {
      setError(err);
      setState('error');
    }
  }, [deckId, navigate, startSession]);

  useEffect(() => {
    if (startedRef.current === deckId) return;
    startedRef.current = deckId;
    open();
  }, [deckId, open]);

  return (
    <div className="etch-view">
      <div className="etch-column">
        {state === 'loading' && (
          <div className="etch-stack">
            <p className="etch-visually-hidden" role="status">
              {t('states.loading')}
            </p>
            <Skeleton height="1.5rem" width="50%" />
            <Skeleton height="10rem" />
          </div>
        )}

        {state === 'empty' && (
          <EmptyState
            title={t('learn.noDue')}
            action={
              <Button variant="secondary" onClick={() => navigate('/missions')}>
                {t('missions.backToCenter')}
              </Button>
            }
          >
            {t('missions.reviewEmptyBody')}
          </EmptyState>
        )}

        {state === 'error' && (
          <Alert tone="danger" title={t('states.error')}>
            <p>{error?.message || t('missions.reviewFailedBody')}</p>
            <div className="etch-row">
              <Button variant="secondary" size="compact" onClick={open}>
                {t('states.retry')}
              </Button>
              <Button variant="ghost" size="compact" onClick={() => navigate('/missions')}>
                {t('missions.backToCenter')}
              </Button>
            </div>
          </Alert>
        )}
      </div>
    </div>
  );
}

export default DeckReviewLauncher;
